import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import api from '../../api';
import { AlertTriangle } from 'lucide-react';
import { ActionDialog, Loading } from './ActionDialog';
import { VITAL_RULES, LIMITS_TEXT } from './vitalLimits';

const COLS = [['temperature', 'Temp'], ['pulse', 'Pulse'], ['respiration', 'Resp'], ['bloodPressure', 'BP'], ['oxygenSaturation', 'O2 %'], ['weight', 'Weight'], ['bloodSugar', 'Sugar'], ['pain', 'Pain']];
const LINES = [['systolic', '#1e40af', 'Systolic'], ['diastolic', '#60a5fa', 'Diastolic'], ['pulseNum', '#dc2626', 'Pulse'], ['oxygenSaturationNum', '#16a34a', 'O2 %']];

export const VitalsDialog = ({ patientId, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/patients/${patientId}/vitals`).then(r => setRows(r.data.vitals || [])).catch(() => setRows([])); }, [patientId]);
  return (
    <ActionDialog title="Vitals Dashboard" onClose={onClose} testid="vitals-dialog" wide>
      {!rows ? <Loading /> : !rows.length ? (
        <p className="text-sm text-gray-600" data-testid="vitals-empty">No vital signs recorded yet. Vitals from Nurses Notes visits appear here automatically.</p>
      ) : (
        <>
          <OutOfRangeSummary rows={rows} />
          <div className="h-64 mb-4" data-testid="vitals-chart">
            <ResponsiveContainer>
              <LineChart data={rows}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
                {LINES.map(([k, c, n]) => <Line key={k} dataKey={k} name={n} stroke={c} connectNulls dot />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vitals-table">
              <thead><tr className="bg-gray-50 text-left text-gray-600"><th className="p-2">Date</th>{COLS.map(([, l]) => <th key={l} className="p-2">{l}</th>)}<th className="p-2">From</th></tr></thead>
              <tbody>{rows.map((r, i) => (
                <tr key={i} className="border-b"><td className="p-2 whitespace-nowrap">{r.date}</td>{COLS.map(([k]) => <VitalCell key={k} k={k} v={r[k]} i={i} />)}<td className="p-2 text-xs text-gray-500">{r.source}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </ActionDialog>
  );
};

const VitalCell = ({ k, v, i }) => {
  const flag = v != null && v !== '' && VITAL_RULES[k]?.(v);
  return (
    <td className={`p-2 ${flag ? 'bg-red-50 text-red-700 font-bold' : ''}`} title={flag || ''} data-testid={flag ? `vital-flag-${k}-${i}` : undefined}>
      {v ?? ''}{flag ? ' !' : ''}
    </td>
  );
};

const OutOfRangeSummary = ({ rows }) => {
  const flags = rows.flatMap(r => COLS.map(([k, l]) => (r[k] != null && r[k] !== '' && VITAL_RULES[k]?.(r[k]) ? `${r.date} ${l} ${r[k]}` : null)).filter(Boolean));
  return (
    <div className={`rounded-lg p-3 mb-3 text-sm ${flags.length ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800'}`} data-testid="vitals-out-of-range-summary">
      {flags.length ? (
        <>
          <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {flags.length} reading{flags.length > 1 ? 's' : ''} outside normal limits</p>
          <p className="mt-1 text-xs">{flags.slice(-6).join(' · ')}</p>
        </>
      ) : <p className="font-medium">All readings within normal limits.</p>}
      <p className="mt-1 text-[11px] text-gray-500">Limits: {LIMITS_TEXT}</p>
    </div>
  );
};
